/**
 * Read more/less text expansion component
 * Truncates content with expand/collapse functionality based on character limit or snippet marker
 */

export class ReadMore extends HTMLElement {
  selectors: { ACTIVE_CLASS: string; READ_MORE_CLASS: string }
  options: { snippet: string; limit: number }
  content: { before: string; after: string }
  label: { readMore: string; readLess: string }
  labelCustom: { labelExpland: string; labelCollapse: string }
  isExpand: boolean
  buttonLoadMore: any

  constructor() {
    super()

    this.selectors = {
      ACTIVE_CLASS: 'is-active',
      READ_MORE_CLASS: 'read-more'
    }
    this.options = {
      snippet: '[more]',
      limit: Number(this.getAttribute('data-limit')) || 0
    }
    this.content = {
      before: this.innerHTML,
      after: ''
    }
    this.label = {
      readMore: 'read more',
      readLess: 'read less'
    }
    this.labelCustom = {
      labelExpland: this.getAttribute('data-labelExpland') || '',
      labelCollapse: this.getAttribute('data-labelCollapse') || ''
    }
    this.isExpand = false

    this.setupButtonLoadMore()
    this.getOptions()
    this.setupLoadMore().then(this.collapse.bind(this))

    if (this.innerHTML.indexOf(this.options.snippet) !== -1) {
      this.classList.add(this.selectors.READ_MORE_CLASS)
    }
  }

  connectedCallback() {
    this.bindEvents()
  }

  getOptions() {
    this.options.snippet = this.dataset.snippet || this.options.snippet
    this.options.limit = this.dataset.limit ? parseInt(this.dataset.limit) : this.options.limit
  }

  bindEvents() {
    this.buttonLoadMore.addEventListener('click', () => {
      if (!this.isExpand) {
        this.expand()
      } else this.collapse()
    })
  }

  setupButtonLoadMore() {
    this.buttonLoadMore = document.createElement('span')
    this.buttonLoadMore.classList = this.getAttribute('data-btn-loadmore-class')
  }

  setupLoadMore() {
    return new Promise<void>((resolve) => {
      if (this.options.limit > 0) {
        this.splitByLimit(resolve)
      } else if (this.innerHTML.indexOf(this.options.snippet) !== -1) {
        this.content.before = this.innerHTML.split(this.options.snippet)[0]
        this.content.after = this.innerHTML.split(this.options.snippet)[1]
        resolve()
      }
    })
  }

  splitByLimit(resolve: { (value: void | PromiseLike<void>): void; (): void; }) {
    const limit = Number(this.options.limit)

    const tokens = this.innerHTML.split(/(<[^>]+>)/g).filter((s) => s.length)
    const isTag = (s: string) => /^<[^>]+>$/.test(s)

    const visibleLength = tokens
      .filter((s) => !isTag(s))
      .reduce((total, s) => total + s.length, 0)

    if (visibleLength <= limit) return

    let count = 0
    let reached = false
    let before = ''
    let after = ''

    tokens.forEach((token) => {
      if (reached) {
        after += token
        return
      }

      if (isTag(token)) {
        before += token
        return
      }

      if (count + token.length <= limit) {
        before += token
        count += token.length
      } else {
        const remaining = limit - count
        before += token.slice(0, remaining)
        after += token.slice(remaining)
        count = limit
        reached = true
      }
    })

    this.content.before = before
    this.content.after = after
    resolve()
  }

  // Helper to append a node (and optional prefix text) to the last element or wrapper
  appendToLast(wrapper: HTMLElement, node: Node, prefix: string = '') {
    const lastElement = wrapper.lastElementChild
    if (lastElement && lastElement.nodeType === Node.ELEMENT_NODE) {
      if (prefix) lastElement.appendChild(document.createTextNode(prefix))
      lastElement.appendChild(node)
    } else {
      if (prefix) wrapper.appendChild(document.createTextNode(prefix))
      wrapper.appendChild(node)
    }
  }

  expand() {
    this.isExpand = true
    const wrapper = document.createElement('div')
    wrapper.innerHTML = this.content.before + this.content.after
    this.buttonLoadMore.innerHTML = this.labelCustom.labelExpland !== '' ? this.labelCustom.labelExpland : this.label.readLess
    this.appendToLast(wrapper, this.buttonLoadMore, ' ')
    this.innerHTML = wrapper.innerHTML
    this.classList.add(this.selectors.ACTIVE_CLASS)
    // Re-attach event listener to the new button
    const newButton = this.querySelector('span.' + this.buttonLoadMore.className.replace(/ /g, '.'))
    if (newButton) {
      newButton.addEventListener('click', () => {
        if (!this.isExpand) {
          this.expand()
        } else {
          this.collapse()
        }
      })
    }
  }

  collapse() {
    this.isExpand = false
    const wrapper = document.createElement('div')
    wrapper.innerHTML = this.content.before
    this.buttonLoadMore.innerHTML = this.labelCustom.labelCollapse !== '' ? this.labelCustom.labelCollapse : this.label.readMore
    this.appendToLast(wrapper, this.buttonLoadMore, '\u2026 ')
    this.innerHTML = wrapper.innerHTML
    this.classList.remove(this.selectors.ACTIVE_CLASS)
    // Re-attach event listener to the new button
    const newButton = this.querySelector('span.' + this.buttonLoadMore.className.replace(/ /g, '.'))
    if (newButton) {
      newButton.addEventListener('click', () => {
        if (!this.isExpand) {
          this.expand()
        } else {
          this.collapse()
        }
      })
    }
  }
}
